import { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Eye, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { isReadOnlyExecutive, RoleLabels, hasAnyRole, OperationsPrincipals, type Role } from "../auth/roles";
import { PersonaSwitcher } from "./PersonaSwitcher";
import { PrintBrand } from "./print/PrintBrand";
import { AccountMenu } from "./header/AccountMenu";
import { HeaderActions, HeaderSearchSlot, useHeaderToolsShown } from "./header/HeaderTools";
import { KeyboardLayer } from "./header/KeyboardLayer";
import { hubsFor, isHubActive, type VisibleHub } from "./hub/hubs";
import { HubFrame } from "./hub/HubPages";
import { TickerSlot } from "./ticker/TickerSlot";
import { useUsageRecorder } from "../pages/platform/usageRecorder";
import { useDataVersionRefresh } from "../hooks/useDataVersionRefresh";
import { PageTransition, motionPersonOf, useApplyMotionPreference } from "./motion";
import { preloadRoute } from "../lib/preload";
import { PhoneLayer } from "./phone/PhoneLayer";
import { HomeLogoLink } from "./home/HomeLogoLink";
import { Toaster } from "./toast/Toaster";
import { usePlanningCalendarSwitch } from "../pages/planning/calendar/calendarSwitch";
import { useRelease160Switches } from "../lib/release160Switches";

// VIPER's floating launcher is loaded after the page, so the chat never slows first paint (VIPER_BUILD_PROMPT §6).
const ViperLauncherSlot = lazy(async () => ({ default: (await import("./viper/ViperLauncherSlot")).ViperLauncherSlot }));
// "Pilot feedback" (release 1.2.0, O-R34): loaded after the page too; the screenshot library never slows first paint.
const PilotFeedbackButton = lazy(async () => ({ default: (await import("../pages/pilot/PilotFeedbackButton")).PilotFeedbackButton }));
// Chart drill-down side panel (release 1.4.0, O-R46): one host for every chart; loaded after the page.
const ChartDrillHost = lazy(async () => ({ default: (await import("./charts/DrillPanel")).ChartDrillHost }));
// Release 1.5.0 page tools (Pin, Make this my start page, Saved filters; O-R64–O-R66): nothing renders when the switch is off.
const PageToolsSlot = lazy(async () => ({ default: (await import("./home/PageTools")).PageTools }));

// Application shell, ported from Summit Engineering: a navy brand-900 sidebar with the wordmark and one link per hub
// the signed-in person can use (About at the foot), the hub's tab strip above every page of that hub, a sticky header,
// a skip link, and below `md` an off-canvas drawer behind the header's Menu button. Only what the role may open is
// shown; the API refuses the rest anyway.

export { NAV, navFor, screensFor, type NavItem, type NavSection } from "./hub/hubs";

function Wordmark() {
  return (
    <div className="flex items-center gap-2.5 px-4 py-4">
      <img src="/favicon/apple-touch-icon.png" alt="" width={28} height={28} className="h-7 w-7 rounded-full ring-1 ring-white/10" />
      <div className="leading-tight">
        <div className="text-sm font-semibold tracking-tight text-white">Summit Operations</div>
        <div className="text-[11px] text-brand-100/80">Summit Aviation, Inc.</div>
      </div>
    </div>
  );
}

function HubLink({ entry, pathname, compact }: { entry: VisibleHub; pathname: string; compact?: boolean }) {
  const active = isHubActive(entry.hub, pathname);
  const Icon = entry.hub.icon;
  return (
    <Link
      to={entry.to}
      data-hub-id={entry.hub.id}
      data-testid={`hub-link-${entry.hub.id}`}
      aria-current={active ? "page" : undefined}
      onMouseEnter={() => preloadRoute(entry.to)}
      onFocus={() => preloadRoute(entry.to)}
      className={`flex items-center gap-2 rounded-md px-2 transition-colors ${compact ? "py-1 text-xs" : "py-1.5 text-sm"} ${
        active ? "bg-white/15 font-medium text-white" : "text-brand-50/85 hover:bg-white/10 hover:text-white"
      }`}
    >
      <Icon aria-hidden="true" className={`${compact ? "h-3.5 w-3.5" : "h-4 w-4"} flex-none`} />
      {entry.hub.label}
    </Link>
  );
}

export function AppShell({ children }: { children?: ReactNode }) {
  const { principal, signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const roles = principal?.roles ?? [];
  const readOnlyExecutive = isReadOnlyExecutive(roles);
  // Only people with an Operations role record screen visits; someone without a role is only asking for access.
  useUsageRecorder(Boolean(principal && principal.roles.length > 0));
  // D-O93: refetch the open page when any data changes (GSS import or an edit by anyone); the kiosk has its own board.
  useDataVersionRefresh(roles.some((r) => r !== "WallDisplay"));
  // Release 1.4.0: the person's "Reduce motion" choice (My profile) applies on every page.
  useApplyMotionPreference(motionPersonOf(principal));
  // Release 1.6.0: learn the Planning Calendar switch after sign-in, so its menu tab appears only when it is on.
  usePlanningCalendarSwitch(Boolean(principal && principal.roles.length > 0));
  useRelease160Switches(Boolean(principal && principal.roles.length > 0));
  // Release 1.5.0 header (search, Go to, sync chip, Refresh, account menu); off = the 1.4.0 header exactly.
  const headerTools = useHeaderToolsShown();
  const roleLabel = principal
    ? (principal.assignedRoles?.length ? principal.assignedRoles : principal.roles).map((r) => RoleLabels[r as Role] ?? r).join(" · ")
    : "";

  const hubs = hubsFor(roles);
  const mainHubs = hubs.filter((h) => h.hub.placement === "main");
  const footerHubs = hubs.filter((h) => h.hub.placement === "footer");

  // Phone: the menu is an off-canvas drawer behind the header's menu button (desktop keeps the fixed column).
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLElement | null>(null);
  const closeMenu = useCallback((returnFocus: boolean) => {
    setMenuOpen(false);
    if (returnFocus) window.setTimeout(() => menuButtonRef.current?.focus(), 0);
  }, []);
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    closeButtonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeMenu(true);
        return;
      }
      // The drawer keeps the focus while it is open: Tab cycles inside it, as a modal must.
      if (e.key !== "Tab" || !drawerRef.current) return;
      const focusable = drawerRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!drawerRef.current.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen, closeMenu]);

  return (
    <div className="flex min-h-screen">
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-white px-3 py-2 text-sm font-medium text-brand-700 shadow focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
      >
        Skip to content
      </a>

      {menuOpen && (
        <div className="fixed inset-0 z-[55] bg-slate-900/40 print:hidden md:hidden" aria-hidden="true" onClick={() => closeMenu(true)} data-testid="menu-backdrop" />
      )}
      <aside
        id="app-sidebar"
        ref={drawerRef}
        className={`fixed inset-y-0 left-0 z-[60] flex h-screen w-64 flex-none flex-col bg-brand-900 text-brand-50 shadow-xl transition-transform duration-200 motion-reduce:transition-none print:hidden md:sticky md:top-0 md:z-auto md:w-56 md:translate-x-0 md:shadow-none ${
          menuOpen ? "translate-x-0" : "-translate-x-full max-md:invisible"
        }`}
        aria-label="Primary"
      >
        <div className="flex items-center justify-between">
          <HomeLogoLink>
            <Wordmark />
          </HomeLogoLink>
          <button
            ref={closeButtonRef}
            type="button"
            className="mr-3 rounded-md p-1.5 text-brand-50/85 hover:bg-white/10 hover:text-white md:hidden"
            aria-label="Close menu"
            onClick={() => closeMenu(true)}
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
        {/* The menu scrolls (wheel, touch, keyboard) when a short window needs it, without showing a scroll bar. */}
        <nav className="scrollbar-none flex-1 overflow-y-auto overscroll-contain px-2 py-2" aria-label="Main navigation">
          <ul className="space-y-0.5">
            {mainHubs.map((entry) => (
              <li key={entry.hub.id}>
                <HubLink entry={entry} pathname={pathname} />
              </li>
            ))}
          </ul>
        </nav>
        {footerHubs.length > 0 && (
          <nav className="px-2 pb-1" aria-label="About Summit Operations">
            <ul>
              {footerHubs.map((entry) => (
                <li key={entry.hub.id}>
                  <HubLink entry={entry} pathname={pathname} compact />
                </li>
              ))}
            </ul>
          </nav>
        )}
        <div className="border-t border-white/10 px-4 py-3 text-[11px] text-brand-100/70">© 2026 Summit Aviation, Inc.</div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className={`sticky top-0 z-30 flex h-12 min-w-0 items-center justify-end overflow-hidden ${headerTools ? "gap-1.5" : "gap-2"} border-b border-slate-200/80 bg-white/90 px-3 backdrop-blur print:hidden md:gap-3 md:px-6`}>
          {principal && (
            <button
              ref={menuButtonRef}
              type="button"
              className="inline-flex flex-none items-center rounded-md border border-slate-200 p-1.5 text-slate-700 hover:bg-slate-100 md:hidden"
              aria-label="Menu"
              aria-expanded={menuOpen}
              aria-controls="app-sidebar"
              onClick={() => (menuOpen ? closeMenu(true) : setMenuOpen(true))}
              data-testid="menu-button"
            >
              <Menu aria-hidden="true" className="h-4 w-4" />
            </button>
          )}
          {principal && <HeaderSearchSlot />}
          <div className="mr-auto min-w-0 flex-1" data-testid="ticker-mount">
            <TickerSlot />
          </div>
          {principal && <HeaderActions />}
          {/* Development persona switcher. With the 1.5.0 header a phone has no room for it: sign out to change persona. */}
          <div className="contents max-lg:hidden">
            <PersonaSwitcher />
          </div>
          {readOnlyExecutive && (
            <span
              className="hidden items-center gap-1 rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700 sm:inline-flex"
              title="Executive (read-only): you can look at everything on your menu; nothing can be changed from this sign-in."
              data-testid="read-only-pill"
            >
              <Eye aria-hidden="true" className="h-3 w-3" />
              Read-only
            </span>
          )}
          {principal && headerTools && (
            <AccountMenu
              name={principal.displayName}
              roleLabel={roleLabel}
              roles={roles}
              upn={principal.userPrincipalName}
              onSignOut={() => {
                signOut();
                navigate("/sign-in", { replace: true });
              }}
            />
          )}
          {principal && !headerTools && (
            <>
              <div className="hidden text-right leading-tight sm:block">
                <div className="text-sm font-medium text-slate-800" data-testid="signed-in-name">
                  {principal.displayName}
                </div>
                <div className="text-[11px] text-slate-500" data-testid="signed-in-roles">
                  {roleLabel}
                </div>
              </div>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-1.5 !px-2.5"
                onClick={() => {
                  signOut();
                  navigate("/sign-in", { replace: true });
                }}
              >
                <LogOut aria-hidden="true" className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </>
          )}
        </header>
        <PrintBrand />
        {/* Keyboard shortcuts and palettes: 1.5.0 search (Ctrl+K), Go to (Ctrl+P), g-chords; 1.4.0 Ctrl+K palette when off (D-O76). */}
        {principal && <KeyboardLayer />}
        {/* Below md a wide table scrolls inside the page area, never the whole page. */}
        <main id="main" tabIndex={-1} className="min-w-0 flex-1 px-3 pb-28 pt-5 outline-none print:pb-5 sm:px-6 max-md:relative max-md:overflow-x-auto">
          <HubFrame>
            {principal && (
              <Suspense fallback={null}>
                <PageToolsSlot />
              </Suspense>
            )}
            <PageTransition>{children ?? <Outlet />}</PageTransition>
          </HubFrame>
        </main>
        {principal && (
          <Suspense fallback={null}>
            <ChartDrillHost />
          </Suspense>
        )}
        {principal && (
          <Suspense fallback={null}>
            <ViperLauncherSlot />
          </Suspense>
        )}
        {principal && hasAnyRole(roles, OperationsPrincipals) && (
          <Suspense fallback={null}>
            <PilotFeedbackButton />
          </Suspense>
        )}
        {/* Release 1.5.0 phone mode (bottom tabs, scan, problem report) and offline read; nothing when the switch is off. */}
        {principal && <PhoneLayer />}
        {/* Release 1.5.0 polish: toasts, including "Undo" for personal actions (O-R67); nothing when the switch is off. */}
        {principal && <Toaster />}
      </div>
    </div>
  );
}
